import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ref, createRef, type Ref } from 'lit/directives/ref.js';
import * as Cesium from 'cesium';
import { twoline2satrec } from 'satellite.js';
import { getGroupTles, getGnssMembership, getDecayTraces, getAmateurStations, ApiError } from '../api/client';
import { Clock } from '../time/Clock';
import { createImageryProvider } from './imagery';
import { AuroraOverlayLayer } from './AuroraOverlayLayer';
import { GroundStationLayer } from './GroundStationLayer';
import { LightPollutionLayer } from './LightPollutionLayer';
import { AmateurStationsLayer } from './AmateurStationsLayer';
import { ConstellationLayer } from './ConstellationLayer';
import { DecayTraceLayer } from './DecayTraceLayer';
import { MarqueeRibbonLayer } from './MarqueeRibbonLayer';
import { MarqueeShapeLayer } from './MarqueeShapeLayer';
import { OrbitRibbonLayer } from './OrbitRibbonLayer';
import { PointPrimitiveLayer } from './PointPrimitiveLayer';
import { SelectionController } from './SelectionController';
import { getSharedOverlays, type OverlayState } from '../overlays/OverlayService';
import { ConjunctionScene } from './ConjunctionScene';
import type { ReplayContext } from '../replay/replayContext';
import {
  aimSkyCameraAt,
  aimSkyHeadingDeg,
  setSkyView,
  setGlobeView,
  snapshotCamera,
  type CameraSnapshot,
  type SkyObserver,
} from './SkyCamera';
import { SkyHorizonLayer } from './SkyHorizonLayer';
import { PassArcLayer, type PassArcRecord } from './PassArcLayer';
import { getSkyViewPasses, getSatellitePasses } from '../api/client';
import { prefersReducedMotion } from '../util/motion';

/** The CelesTrak group we render at chunk 5; chunk 6+ may parameterize this. */
const DEFAULT_GROUP = 'active';

/**
 * Plain class wrapping Cesium.Viewer + the satellite-rendering pipeline.
 * Lit-element-agnostic — the imperative Cesium API doesn't benefit from
 * Lit's reactive render cycle.
 */
export class Globe {
  public viewer?: Cesium.Viewer;
  public layer?: PointPrimitiveLayer;
  public ribbons?: OrbitRibbonLayer;
  public marquee?: MarqueeShapeLayer;
  /** Phase 9 chunk 3 — always-on sun-synch context ribbons. */
  public sunSynchRibbons?: MarqueeRibbonLayer;
  /** Phase 9 chunk 4 — historical decay-trace overlay. */
  public decayTraces?: DecayTraceLayer;
  /** Phase 9 chunk 4 — guard so we only fetch decay traces once per visibility flip. */
  private decayTracesLoaded = false;
  /** Post-Phase-11 — major constellation stick-figure overlay. */
  public constellations?: ConstellationLayer;
  /** Phase 10 chunk 3 — amateur SatNOGS ground-station overlay. */
  public amateurStations?: AmateurStationsLayer;
  /** Phase 10 chunk 3 — guard so we only fetch the ~2,000-row catalog once. */
  private amateurStationsLoaded = false;
  public stations?: GroundStationLayer;
  public lightPollution?: LightPollutionLayer;
  public aurora?: AuroraOverlayLayer;
  public selection?: SelectionController;
  public clock?: Clock;
  /** Phase 6 chunk 1 — non-null while a conjunction replay is active. */
  public conjunctionScene?: ConjunctionScene;
  /** Phase 7 chunk 1B — non-null while sky view is active; holds the
   *  snapshotted globe-view camera state so `exitSkyView()` can restore
   *  it cleanly. */
  private skyViewSnapshot: CameraSnapshot | null = null;
  /** Phase 7 chunk 3E — observer recorded on sky-view entry so
   *  `aimSkyViewAt` can compute local-ENU heading/pitch without
   *  threading the observer through every selection call site. */
  private skyViewObserver: SkyObserver | null = null;
  /** Phase 7 chunk 1B — prior atmosphere visibility, captured on
   *  sky-view entry so exit can restore it instead of forcing show=true. */
  private priorAtmosphereShow: boolean | null = null;
  /** Phase 7 chunk 1B — prior terrain-collision floor, captured on
   *  sky-view entry so exit can restore the user's collision setting. */
  private priorCollisionHeight: number | null = null;
  /** Phase 7 chunk 2B — N/E/S/W horizon labels + zenith reticle.
   *  Attached on sky-view enter, detached on exit. */
  private skyHorizon: SkyHorizonLayer = new SkyHorizonLayer();
  /** Phase 7 chunk 3B — pass-arc layer for marquee satellites passing
   *  over the observer in the next 24h. Attached on sky-view enter,
   *  populated async after the /api/v1/sky-view/passes fetch resolves. */
  private passArcs: PassArcLayer = new PassArcLayer();
  /** Token-rev'd on each sky-view enter so a slow in-flight pass
   *  fetch from a *previous* sky-view session can't race-write into
   *  a later one. */
  private passArcFetchToken = 0;
  /** Phase 7 chunk 2C — removeListener handle for the camera-changed
   *  subscription that drives the `sky-camera-heading` window event.
   *  Non-null only while sky view is active. */
  private skyHeadingUnsub: (() => void) | null = null;
  /** Last-emitted heading in degrees; suppresses duplicate dispatches
   *  when the camera nudges by less than 0.5°. */
  private lastEmittedHeadingDeg: number | null = null;
  private ribbonTickUnsub: (() => void) | null = null;
  private marqueeTickUnsub: (() => void) | null = null;
  private overlayUnsub: (() => void) | null = null;
  private selectedNorad: number | null = null;
  private overlayState: OverlayState = {
    ribbons: true, marquee: true, stations: false, lightPollution: false, aurora: false,
    gnssGps: false, gnssGalileo: false, gnssGlonass: false, gnssBeidou: false,
    sunSynchRibbons: false, decayTrails: false, amateurStations: false,
    constellations: false, passArcs: true,
  };

  async init(
    container: HTMLElement,
    opts: {
      cesiumIonToken: string;
      onSelect: (norad: number | null) => void;
      onStationSelect?: (pick: { stationId: string; name: string }, screenPos: { x: number; y: number }) => void;
      /** Phase 9 chunk 4 — called when a decay-trace polyline is clicked. */
      onDecayTraceSelect?: (pick: { norad: number; name: string }) => void;
      /** Phase 10 chunk 3 — called when an amateur SatNOGS dot is clicked. */
      onAmateurStationSelect?: (pick: { stationId: string; name: string; detailsUrl: string; qfLocator: string | null }, screenPos: { x: number; y: number }) => void;
      onStatus: (status: string) => void;
      onClockReady?: (clock: Clock) => void;
    },
  ): Promise<void> {
    const provider = await createImageryProvider(opts.cesiumIonToken);

    const viewer = new Cesium.Viewer(container, {
      animation: false,
      baseLayerPicker: false,
      fullscreenButton: false,
      geocoder: false,
      homeButton: false,
      navigationHelpButton: false,
      sceneModePicker: false,
      selectionIndicator: false,
      timeline: false,
      infoBox: false,
      baseLayer: new Cesium.ImageryLayer(provider, {}),
    });
    this.viewer = viewer;

    viewer.scene.globe.enableLighting = true;
    if (viewer.scene.skyAtmosphere) {
      viewer.scene.skyAtmosphere.show = true;
    }
    viewer.scene.fog.enabled = false;
    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString('#0a0e27');

    // Phase 3 chunk 1: BSC5 starfield + explicit sun/moon.  The
    // skybox cubemap is generated at build time (see bin/build-skybox.php
    // and the README "Phase 3 — Showcase visuals" entry); each face is a
    // 1024² PNG of stars at their J2000 positions, magnitude-graded.
    // Cesium loads the textures into a SkyBox primitive that sits in
    // the inertial frame, so the stars track Earth's rotation correctly
    // as the user scrubs time.
    const skyboxBase = '/textures/skybox';
    viewer.scene.skyBox = new Cesium.SkyBox({
      sources: {
        positiveX: `${skyboxBase}/px.png`,
        negativeX: `${skyboxBase}/nx.png`,
        positiveY: `${skyboxBase}/py.png`,
        negativeY: `${skyboxBase}/ny.png`,
        positiveZ: `${skyboxBase}/pz.png`,
        negativeZ: `${skyboxBase}/nz.png`,
      },
    });
    if (viewer.scene.sun) {
      viewer.scene.sun.show = true;
    }
    if (viewer.scene.moon) {
      viewer.scene.moon.show = true;
    }

    this.clock = new Clock(viewer.clock);
    this.layer = new PointPrimitiveLayer(viewer.scene, this.clock);
    this.layer.onStatusChange = opts.onStatus;
    this.ribbons = new OrbitRibbonLayer(viewer.scene);
    this.marquee = new MarqueeShapeLayer(viewer.scene);
    this.sunSynchRibbons = new MarqueeRibbonLayer(viewer.scene);
    this.sunSynchRibbons.setTleSource(this.layer);
    this.decayTraces = new DecayTraceLayer(viewer.scene);
    this.amateurStations = new AmateurStationsLayer(viewer.scene);
    this.constellations = new ConstellationLayer();
    this.constellations.attach(viewer);
    this.stations = new GroundStationLayer(viewer.scene);
    this.lightPollution = new LightPollutionLayer(viewer);
    this.aurora = new AuroraOverlayLayer(viewer);
    this.selection = new SelectionController(viewer, {
      onSelect: opts.onSelect,
      onStationSelect: opts.onStationSelect
        ? (pick, pos) => opts.onStationSelect?.(pick, pos)
        : undefined,
      onDecayTraceSelect: opts.onDecayTraceSelect
        ? (pick) => opts.onDecayTraceSelect?.({ norad: pick.norad, name: pick.name })
        : undefined,
      onAmateurStationSelect: opts.onAmateurStationSelect
        ? (pick, pos) => opts.onAmateurStationSelect?.(
            { stationId: pick.stationId, name: pick.name, detailsUrl: pick.detailsUrl, qfLocator: pick.qfLocator },
            pos,
          )
        : undefined,
    });
    opts.onClockReady?.(this.clock);

    // Phase 3 chunk 4B: subscribe to OverlayService so toggles in the
    // §overlays topbar menu reach every layer.  Sync-emits the current
    // state on subscribe, so the initial visibility is set immediately.
    this.overlayUnsub = getSharedOverlays().subscribe((state) => {
      this.overlayState = state;
      this.stations?.setVisible(state.stations);
      this.lightPollution?.setVisible(state.lightPollution);
      this.aurora?.setVisible(state.aurora);
      // Ribbon + marquee are gated by selection AND the overlay flag;
      // re-apply selection so a flip to "off" tears them down.
      this.setRibbonTarget(this.selectedNorad);
      this.refreshMarquee();
      // Phase 9 chunk 2 — fan out the GNSS flags so PointPrimitiveLayer
      // can recolor constellation members. Debounced inside the layer
      // (50 ms) so rapid-fire toggles don't repaint mid-frame.
      this.layer?.setGnssOverlayFlags({
        gnssGps:     state.gnssGps,
        gnssGalileo: state.gnssGalileo,
        gnssGlonass: state.gnssGlonass,
        gnssBeidou:  state.gnssBeidou,
      });
      // Phase 9 chunk 3 — sun-synch ribbon overlay. Show/hide the
      // whole layer; the layer's update tick handles re-render on
      // its own 30 s clock-time throttle.
      this.sunSynchRibbons?.setVisible(state.sunSynchRibbons, this.clock?.getTimeMs() ?? Date.now());
      // Phase 9 chunk 4 — decay-trace overlay. Lazy-fetch the bundle
      // on first show so the boot path stays light when the user
      // never flips this on. Failure is silent — empty payload =
      // empty layer = no traces, which is also the common case
      // (sparse TIP-message coverage in any given 30-day window).
      if (state.decayTrails && !this.decayTracesLoaded) {
        this.decayTracesLoaded = true;
        void getDecayTraces()
          .then((res) => this.decayTraces?.setTraces(res.data))
          .catch((err) => console.warn('[Globe] decay-trace fetch failed', err));
      }
      this.decayTraces?.setVisible(state.decayTrails);

      // Phase 10 chunk 3 — amateur SatNOGS ground stations. Lazy-fetch
      // on first show so the boot path stays light when the user never
      // flips this on. Active-only by default so the layer respects
      // the chunk-3 2,000-dot perf budget.
      if (state.amateurStations && !this.amateurStationsLoaded) {
        this.amateurStationsLoaded = true;
        void getAmateurStations(1, 2000, false)
          .then((res) => this.amateurStations?.setStations(res.data))
          .catch((err) => console.warn('[Globe] amateur-stations fetch failed', err));
      }
      this.amateurStations?.setVisible(state.amateurStations);

      // Post-Phase-11 — constellation stick-figure overlay. Data is
      // bundled at build time (resources/data/constellations.json),
      // so no lazy-fetch — the toggle just flips primitive visibility.
      this.constellations?.setVisible(state.constellations);

      // Post-Phase-11 — sky-view pass-arc overlay. Same cheap
      // primitive-visibility toggle; the underlying records were
      // already fetched on sky-view entry and cached server-side, so
      // turning the toggle off then on doesn't re-hit the server.
      this.passArcs.setVisible(state.passArcs);
    });

    // Phase 3 chunk 2B: refresh the active orbit ribbon as the user
    // scrubs time.  OrbitRibbonLayer.update() throttles to ~once per
    // 1/30 of the satellite's period, so the cost is bounded.
    this.ribbonTickUnsub = this.clock.onTick((timeMs) => {
      if (this.overlayState.ribbons) this.ribbons?.update(timeMs);
      // Phase 9 chunk 3 — sun-synch context ribbons; the layer's own
      // 30 s throttle keeps cost bounded so we don't need to gate
      // here on the overlay flag (the layer no-ops when invisible).
      this.sunSynchRibbons?.update(timeMs);
    });

    // Phase 3 chunk 3B: track the selected satellite's marquee shape
    // every clock tick (the worker repaints positions ~4Hz; we read
    // back the cached ECEF and reposition the primitive).  No-op
    // when nothing is selected or when the camera is far from it.
    this.marqueeTickUnsub = this.clock.onTick(() => this.refreshMarquee());

    opts.onStatus('Loading satellite catalog…');
    try {
      const bundle = await getGroupTles(DEFAULT_GROUP);
      opts.onStatus(`Parsing ${bundle.count.toLocaleString()} TLEs…`);
      this.layer.load(bundle.tles);
      this.layer.startPropagation();
    } catch (err) {
      const msg = err instanceof ApiError ? `API error ${err.status}` : String(err);
      opts.onStatus(`Failed to load satellites: ${msg}`);
      console.error('Globe failed to load satellites:', err);
    }

    // Phase 9 chunk 2 — fetch GNSS membership in the background so
    // the constellation-color overlays in the §overlays menu are
    // ready when the user opens them. Failure is silent — without
    // membership the overlays simply have no effect.
    void getGnssMembership()
      .then((res) => this.layer?.setGnssMembership(res.data))
      .catch((err) => console.warn('[Globe] GNSS membership fetch failed', err));
  }

  /**
   * Phase 6 chunk 1 — enter conjunction-replay mode. Sets up the chase
   * camera, clock window, and clock pause via {@link ConjunctionScene}.
   * Returns the active scene so callers can dispose later.
   */
  async enterConjunctionReplay(ctx: ReplayContext): Promise<ConjunctionScene> {
    this.conjunctionScene?.dispose();
    const scene = new ConjunctionScene(this, ctx);
    this.conjunctionScene = scene;
    await scene.activate();
    return scene;
  }

  /** Tear down any active conjunction replay. No-op when none active. */
  exitConjunctionReplay(): void {
    this.conjunctionScene?.dispose();
    this.conjunctionScene = undefined;
  }

  /**
   * Phase 7 chunk 1B — flip the camera to the stargazer pose at the
   * observer's location. Snapshots prior camera + atmosphere + terrain
   * collision so {@link exitSkyView} can restore them cleanly. Chunk 2A
   * also suspends ground-stations + light-pollution + aurora overlays
   * (they're below the observer in sky view, irrelevant + cluttering).
   *
   * Idempotent: calling twice without an exit between is a no-op (the
   * second call would clobber the snapshot, leaving exit unable to
   * restore properly). Returns silently if the viewer isn't ready or
   * sky view is already active.
   */
  enterSkyView(observer: SkyObserver): void {
    if (!this.viewer) return;
    if (this.skyViewSnapshot !== null) return;
    this.skyViewSnapshot = snapshotCamera(this.viewer);
    this.skyViewObserver = observer;
    if (this.viewer.scene.skyAtmosphere) {
      this.priorAtmosphereShow = this.viewer.scene.skyAtmosphere.show;
      this.viewer.scene.skyAtmosphere.show = false;
    }
    const ssc = this.viewer.scene.screenSpaceCameraController;
    this.priorCollisionHeight = ssc.minimumCollisionTerrainHeight;
    // Disable terrain collision so the user can rotate freely without
    // the camera snagging at the local horizon. Restored on exit.
    ssc.minimumCollisionTerrainHeight = 0;
    // Phase 7 chunk 2A — suspend the surface overlays that are below
    // the camera in sky view. Ribbons + marquee stay (the selected sat
    // is useful overhead context).
    getSharedOverlays().suspend(['stations', 'lightPollution', 'aurora']);
    setSkyView(this.viewer, observer);
    // Phase 7 chunk 2B — N/E/S/W horizon labels + zenith reticle.
    this.skyHorizon.attach(this.viewer, observer);
    // Phase 7 chunk 2C — dispatch a `sky-camera-heading` window event
    // when the camera rotates so the SkyOverlay's compass strip updates.
    this.attachSkyHeadingListener();
    // Phase 7 chunk 3B — attach the pass-arc layer, then kick off an
    // async fetch of the marquee passes. Arcs appear as soon as the
    // server responds (typically < 200ms thanks to PassCache).
    this.passArcs.attach(this.viewer);
    void this.refreshPassArcs(observer);
  }

  /**
   * Phase 7 chunk 3B — fetch the marquee-pass aggregate from
   * `/api/v1/sky-view/passes` and hand the records to PassArcLayer.
   * Token-rev'd so a stale fetch from an earlier sky-view session
   * can't clobber a newer one's arcs.
   */
  private async refreshPassArcs(observer: SkyObserver): Promise<void> {
    if (!this.layer) return;
    const myToken = ++this.passArcFetchToken;
    let response;
    try {
      response = await getSkyViewPasses({
        lat: observer.latitude,
        lon: observer.longitude,
        withinHours: 24,
        // Phase 7 chunk 4B — request the marquee roster + naked-eye-bright
        // discovery in a single call. Server caps the response at 100 arcs;
        // first call from a never-before-seen observer can take several
        // seconds (PassCalculator over up to 30 RCS-ranked candidates) but
        // subsequent calls hit the 6h pass cache and return in <100ms.
        include: 'bright',
      });
    } catch (err) {
      console.warn('[Globe] sky-view passes fetch failed', err);
      return;
    }
    if (myToken !== this.passArcFetchToken) return;                            // newer call superseded us
    if (!this.passArcs.isAttached()) return;                                   // sky view exited during the fetch
    const records: PassArcRecord[] = [];
    for (const pass of response.data) {
      const tle = this.layer.getTle(pass.norad);
      if (tle === null) continue;                                              // satellite not in the loaded catalog
      records.push({
        norad: pass.norad,
        label: pass.label,
        rise_at: pass.rise_at,
        peak_at: pass.peak_at,
        set_at: pass.set_at,
        max_elevation_deg: pass.max_elevation_deg,
        tle,
      });
    }
    this.passArcs.setMarquee(records);
  }

  /**
   * Phase 7 chunk 3C — refresh the pass-arcs for the currently-selected
   * satellite. Called from `<sat-app>.applySelection` whenever the
   * selection changes. Silent no-op when not in sky view or when the
   * selected sat has no observer / no TLE.
   *
   * Pass `null` to clear the selected-arc slot (deselection).
   */
  async refreshSelectedPassArc(norad: number | null, observer: SkyObserver | null): Promise<void> {
    if (!this.layer || !this.passArcs.isAttached()) return;
    if (norad === null || observer === null) {
      this.passArcs.setSelected([]);
      return;
    }
    const tle = this.layer.getTle(norad);
    const name = this.layer.getName(norad);
    if (tle === null) {
      this.passArcs.setSelected([]);
      return;
    }
    let response;
    try {
      response = await getSatellitePasses(norad, {
        lat: observer.latitude,
        lon: observer.longitude,
        alt: observer.altitudeMeters ?? 0,
        days: 1,
        min_elevation_deg: 10,
      });
    } catch (err) {
      console.warn('[Globe] selected-sat passes fetch failed', err);
      return;
    }
    // The selection may have changed (or sky view may have exited)
    // while the request was in flight.
    if (!this.passArcs.isAttached()) return;
    const records: PassArcRecord[] = response.data.map((pass) => ({
      norad,
      label: name ?? `NORAD ${norad}`,
      rise_at: pass.rise_at,
      peak_at: pass.peak_at,
      set_at: pass.set_at,
      max_elevation_deg: pass.max_elevation_deg,
      tle,
    }));
    this.passArcs.setSelected(records);
  }

  /** Phase 7 chunk 2C — subscribe to camera changes while sky view is
   *  active and emit a debounced `sky-camera-heading` window event. */
  private attachSkyHeadingListener(): void {
    if (!this.viewer || this.skyHeadingUnsub !== null) return;
    const camera = this.viewer.camera;
    const emit = (): void => {
      const headingDeg = (camera.heading * 180) / Math.PI;
      const normalized = ((headingDeg % 360) + 360) % 360;
      if (
        this.lastEmittedHeadingDeg !== null &&
        Math.abs(normalized - this.lastEmittedHeadingDeg) < 0.5 &&
        Math.abs(normalized - this.lastEmittedHeadingDeg) < 359.5
      ) {
        return;
      }
      this.lastEmittedHeadingDeg = normalized;
      window.dispatchEvent(
        new CustomEvent('sky-camera-heading', { detail: { headingDeg: normalized } }),
      );
    };
    // Emit once immediately so consumers don't wait for the next pan.
    emit();
    const remove = camera.changed.addEventListener(emit);
    this.skyHeadingUnsub = (): void => {
      remove();
      this.lastEmittedHeadingDeg = null;
    };
  }

  private detachSkyHeadingListener(): void {
    if (this.skyHeadingUnsub !== null) {
      this.skyHeadingUnsub();
      this.skyHeadingUnsub = null;
    }
  }

  /**
   * Phase 7 chunk 1B — tear down sky view, restoring camera, atmosphere,
   * terrain-collision state, and (chunk 2A) any suspended overlays.
   * No-op when sky view isn't active.
   */
  exitSkyView(): void {
    if (!this.viewer || this.skyViewSnapshot === null) return;
    setGlobeView(this.viewer, this.skyViewSnapshot);
    if (this.viewer.scene.skyAtmosphere && this.priorAtmosphereShow !== null) {
      this.viewer.scene.skyAtmosphere.show = this.priorAtmosphereShow;
    }
    if (this.priorCollisionHeight !== null) {
      this.viewer.scene.screenSpaceCameraController.minimumCollisionTerrainHeight =
        this.priorCollisionHeight;
    }
    // Phase 7 chunk 2A — restore the overlays we suspended on entry.
    getSharedOverlays().restore();
    // Phase 7 chunk 2B — tear down horizon labels + zenith reticle.
    this.skyHorizon.detach();
    // Phase 7 chunk 2C — stop emitting sky-camera-heading events.
    this.detachSkyHeadingListener();
    // Phase 7 chunk 3B — tear down marquee pass arcs. Bumping the
    // fetch token additionally guarantees any in-flight request
    // can't write into the layer after this point.
    this.passArcFetchToken++;
    this.passArcs.detach();
    this.skyViewSnapshot = null;
    this.skyViewObserver = null;
    this.priorAtmosphereShow = null;
    this.priorCollisionHeight = null;
  }

  /**
   * Phase 7 chunk 3E — rotate the sky-view camera to aim at a
   * specific satellite (looked up from the layer's cached ECEF
   * position). Silent no-op when not in sky view OR the satellite
   * position hasn't been propagated yet OR the worker hasn't
   * reported a position for that NORAD.
   *
   * Camera position stays at the observer + eye-height; only
   * heading + pitch animate over ~0.8s. The selected dot's chunk-3A
   * pulse animation is what makes the satellite legible once
   * aimed at.
   */
  aimSkyViewAt(norad: number): void {
    if (!this.viewer || this.skyViewSnapshot === null || this.skyViewObserver === null) return;
    const satPos = this.layer?.getPosition(norad);
    if (!satPos) return;
    aimSkyCameraAt(this.viewer, this.skyViewObserver, satPos);
  }

  /**
   * Phase 11 follow-up — rotate the sky-view camera to face a given
   * compass heading (0 = N, 90 = E, 180 = S, 270 = W). Preserves the
   * user's current pitch + FOV. Silent no-op when not in sky view.
   *
   * Wired to the chunk-2C compass HUD cells (now buttons): clicking
   * "E" calls this with 90.
   */
  setSkyHeadingDeg(headingDeg: number): void {
    if (!this.viewer || this.skyViewSnapshot === null || this.skyViewObserver === null) return;
    if (!Number.isFinite(headingDeg)) return;
    aimSkyHeadingDeg(this.viewer, this.skyViewObserver, headingDeg);
  }

  /** True while sky view is active. Read by the chunk-1C topbar
   *  toggle to render the right button state. */
  isInSkyView(): boolean {
    return this.skyViewSnapshot !== null;
  }

  /**
   * Animate the camera to a position above the satellite, looking
   * straight down. Uses the satellite's most recent ECEF position
   * (whichever the worker last reported); does not continuously track.
   *
   * Phase 8 chunk 1 — when reduced-motion is requested, the flight
   * duration is forced to 0 (an instant cut, equivalent to setView).
   */
  flyToSatellite(norad: number, durationSeconds = 1.5): void {
    if (!this.viewer || !this.layer) return;
    const pos = this.layer.getPosition(norad);
    if (pos === null) return; // worker hasn't reported a position yet

    const carto = Cesium.Cartographic.fromCartesian(pos);
    const lat = Cesium.Math.toDegrees(carto.latitude);
    const lon = Cesium.Math.toDegrees(carto.longitude);
    const camHeight = carto.height + 3_000_000; // 3000km above sat

    this.viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(lon, lat, camHeight),
      orientation: { heading: 0, pitch: -Math.PI / 2, roll: 0 },
      duration: prefersReducedMotion() ? 0 : durationSeconds,
    });
  }

  /**
   * Show / hide the orbit ribbon for a NORAD.  Pulls the raw TLE
   * lines back from PointPrimitiveLayer (loaded at startup) and
   * builds a fresh satrec on the main thread for the ribbon — the
   * worker has its own satrecs but they're not transferable.
   * Pass null to clear.
   */
  setRibbonTarget(norad: number | null): void {
    if (!this.ribbons || !this.layer || !this.clock) return;
    if (norad === null || !this.overlayState.ribbons) {
      this.ribbons.hide();
      return;
    }
    const tle = this.layer.getTle(norad);
    if (tle === null) {
      this.ribbons.hide();
      return;
    }
    const satrec = twoline2satrec(tle.line1, tle.line2);
    this.ribbons.show(satrec, this.clock.getTimeMs());
  }

  /** Track the selected NORAD for the marquee model overlay. */
  setMarqueeTarget(norad: number | null): void {
    this.selectedNorad = norad;
    if (norad === null) {
      this.marquee?.hide();
      return;
    }
    this.refreshMarquee();
  }

  private refreshMarquee(): void {
    if (!this.marquee) return;
    const norad = this.selectedNorad;
    if (norad === null || !this.overlayState.marquee) {
      this.marquee.hide();
      return;
    }
    const position = this.layer?.getPosition(norad) ?? null;
    const name     = this.layer?.getName(norad) ?? null;
    this.marquee.update(norad, name, position);
  }

  /**
   * Phase 4 chunk 6D — count satellites currently above a given
   * elevation from an observer at (lat°, lon°, alt_m).  Reads the
   * worker's most-recent cached ECEF positions; cost is one vector
   * subtract + two dot products per satellite (~15k iterations, sub-
   * 50ms in practice).  Returns 0 if the point layer hasn't loaded.
   */
  countSatellitesAboveStation(latDeg: number, lonDeg: number, altMeters = 0, minElevationDeg = 5): number {
    return this.noradsAboveStation(latDeg, lonDeg, altMeters, minElevationDeg).size;
  }

  /**
   * Phase 7 chunk 3D — set of NORADs currently above the given
   * elevation from an observer. Same math as
   * {@link countSatellitesAboveStation} but returns the membership
   * so the sky-view search can post-filter autocomplete results
   * down to "what's actually visible from here right now."
   *
   * Returns an empty Set if the point layer hasn't loaded yet.
   */
  noradsAboveStation(latDeg: number, lonDeg: number, altMeters = 0, minElevationDeg = 5): Set<number> {
    const out = new Set<number>();
    if (!this.layer) return out;
    const obsCart = Cesium.Cartesian3.fromDegrees(lonDeg, latDeg, altMeters);
    const enuFromWorld = Cesium.Matrix4.inverseTransformation(
      Cesium.Transforms.eastNorthUpToFixedFrame(obsCart),
      new Cesium.Matrix4(),
    );
    const minSinEl = Math.sin((minElevationDeg * Math.PI) / 180);

    const rel = new Cesium.Cartesian3();
    const enu = new Cesium.Cartesian3();
    for (const [norad, position] of this.layer.iteratePositions()) {
      Cesium.Cartesian3.subtract(position, obsCart, rel);
      const range = Cesium.Cartesian3.magnitude(rel);
      if (range === 0) continue;
      Cesium.Matrix4.multiplyByPointAsVector(enuFromWorld, rel, enu);
      // sin(elevation) = up / range; up component is enu.z.
      if ((enu.z / range) >= minSinEl) {
        out.add(norad);
      }
    }
    return out;
  }

  destroy(): void {
    this.ribbonTickUnsub?.();
    this.ribbonTickUnsub = null;
    this.marqueeTickUnsub?.();
    this.marqueeTickUnsub = null;
    this.overlayUnsub?.();
    this.overlayUnsub = null;
    this.selection?.destroy();
    this.ribbons?.destroy();
    this.marquee?.destroy();
    this.sunSynchRibbons?.destroy();
    this.decayTraces?.destroy();
    this.amateurStations?.destroy();
    this.constellations?.detach();
    this.stations?.destroy();
    this.lightPollution?.destroy();
    this.aurora?.destroy();
    this.layer?.destroy();
    this.viewer?.destroy();
    this.viewer = undefined;
    this.layer = undefined;
    this.ribbons = undefined;
    this.marquee = undefined;
    this.stations = undefined;
    this.lightPollution = undefined;
    this.aurora = undefined;
    this.selection = undefined;
  }
}

@customElement('sat-globe')
export class SatGlobe extends LitElement {
  @property({ type: String, attribute: 'cesium-ion-token' })
  cesiumIonToken = '';

  @state() private status = 'Initializing globe…';
  @state() private loaded = false;

  // Exposed so <sat-app> can call flyToSatellite / layer.getPosition /
  // layer.setHighlight from the host context without leaking the underlying
  // Cesium objects directly.
  public readonly globe = new Globe();
  private containerRef: Ref<HTMLDivElement> = createRef();

  static styles = css`
    :host {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
    }
    .container {
      position: absolute;
      inset: 0;
    }

    /* The widgets.css loaded in shell.php doesn't pierce this Lit
       component's Shadow DOM, so the Cesium viewer's internal divs
       default to a 300×150 canvas inside an unsized .cesium-widget.
       Inline the essential sizing rules so the viewer fills its
       container. */
    .container .cesium-viewer,
    .container .cesium-viewer-cesiumWidgetContainer,
    .container .cesium-widget,
    .container .cesium-widget canvas {
      width: 100%;
      height: 100%;
      display: block;
    }
    .container .cesium-widget canvas {
      touch-action: none;
    }
    .credit {
      position: absolute;
      bottom: 0.4rem;
      right: 0.6rem;
      color: var(--color-text-dim);
      font-family: var(--font-body);
      font-size: 0.7rem;
      pointer-events: auto;
      z-index: 1;
    }
    .credit a {
      color: var(--color-text-muted);
    }
    .status {
      position: absolute;
      bottom: 0.4rem;
      left: 0.6rem;
      padding: 0.25rem 0.6rem;
      background: var(--color-bg-overlay);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      color: var(--color-text-muted);
      font-family: var(--font-mono);
      font-size: 0.75rem;
      pointer-events: none;
      z-index: 1;
      transition: opacity 0.4s ease;
    }
    .status.faded {
      opacity: 0.5;
    }
    /* Hide Cesium's built-in credit container — we render our own. */
    .container :global(.cesium-widget-credits) {
      display: none !important;
    }
  `;

  async firstUpdated(): Promise<void> {
    const container = this.containerRef.value;
    if (!container) return;

    await this.globe.init(container, {
      cesiumIonToken: this.cesiumIonToken,
      onSelect: (norad) => this.dispatchSelect(norad),
      onStationSelect: (pick, pos) => {
        // Phase 4 chunk 6D — bridge the SelectionController callback into
        // a window-level event the <sat-station-tooltip> listens for.
        // Compute the count synchronously against the worker's cached
        // positions so the tooltip never flickers as "Tracking 0".
        const layer = this.globe.stations;
        const catalog = layer?.constructor && (layer.constructor as { catalog?: () => Array<{
          id: string; name: string; latitude_deg: number; longitude_deg: number; altitude_m: number;
        }> }).catalog?.() || [];
        const station = catalog.find((s) => s.id === pick.stationId);
        const count = station
          ? this.globe.countSatellitesAboveStation(station.latitude_deg, station.longitude_deg, station.altitude_m, 5)
          : 0;
        window.dispatchEvent(new CustomEvent('station-pick-info', {
          detail: {
            stationId: pick.stationId,
            name:      pick.name,
            trackingCount: count,
            screenX:   pos.x,
            screenY:   pos.y,
          },
        }));
      },
      onDecayTraceSelect: (pick) => {
        // Phase 9 chunk 4 — bubble a window-level event the App's
        // chunk-4 router catches → /text/satellite/{norad}.
        window.dispatchEvent(new CustomEvent('decay-pick', {
          detail: { norad: pick.norad, name: pick.name },
        }));
      },
      onAmateurStationSelect: (pick, pos) => {
        // Phase 10 chunk 3 — bubble a window-level event the
        // <sat-amateur-station-tooltip> listens for.
        window.dispatchEvent(new CustomEvent('amateur-station-pick-info', {
          detail: {
            stationId:  pick.stationId,
            name:       pick.name,
            detailsUrl: pick.detailsUrl,
            qfLocator:  pick.qfLocator,
            screenX:    pos.x,
            screenY:    pos.y,
          },
        }));
      },
      onStatus: (s) => {
        this.status = s;
        if (s.startsWith('Tracking')) {
          this.loaded = true;
          // Fade the status pill once tracking starts.
          window.setTimeout(() => this.requestUpdate(), 3000);
        }
      },
      onClockReady: (clock) => {
        this.dispatchEvent(
          new CustomEvent('clock-ready', {
            detail: { clock },
            bubbles: true,
            composed: true,
          }),
        );
      },
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.globe.destroy();
  }

  private dispatchSelect(norad: number | null): void {
    this.dispatchEvent(
      new CustomEvent<{ norad: number | null }>('select', {
        detail: { norad },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`
      <div class="container" ${ref(this.containerRef)}></div>
      <div class="status ${this.loaded ? 'faded' : ''}">${this.status}</div>
      <div class="credit">
        ${this.cesiumIonToken
          ? html`<a href="https://cesium.com" target="_blank" rel="noopener">Cesium ion</a>`
          : html`©
              <a
                href="https://www.openstreetmap.org/copyright"
                target="_blank"
                rel="noopener"
                >OpenStreetMap</a
              >
              contributors`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-globe': SatGlobe;
  }
}
